import { useAuth } from '@teko/auth-react';
import { logger } from '@teko/log';
import {
  ExternalType,
  FCM_TOKEN,
  Notification as Noti,
} from '@teko/notification';
import terraKit from '@terra-js/terra-kit';
import { NOTIFICATION_TAB } from 'constants/index';
import { useServices } from 'contexts';
import { useTenantConfig } from 'hooks';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  convertNotification,
  getExternalTypesByPath,
  installNotification,
} from './helper';

export enum PageStatus {
  LOADING = 'loading',
  LOADED = 'loaded',
  EMPTY = 'empty',
  FAILED = 'failed',
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

interface INotificationContext {
  status: PageStatus;
  notifications: Noti[];
  allNotifications: Noti[];
  checkpoint?: number;
  loading: boolean;
  getNotifications: (
    checkpoint?: number,
    limit?: number,
    isNew?: boolean,
    overrideData?: boolean,
  ) => Promise<void>;
  markReadNotification: (notification: Noti) => Promise<void>;
  markReadAll: (externalTypes?: ExternalType[]) => Promise<void>;
  markAllReadLoading?: boolean;
  unRead: number;
  currentTab: NOTIFICATION_TAB;
  setCurrentTab: (currentTab: NOTIFICATION_TAB) => void;
}

export const NotificationContext = React.createContext<
  INotificationContext | undefined
>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used in NotificationProvider');
  }

  return context;
};

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { user, loading: userLoading } = useAuth();
  const { notificationService, discoveryService } = useServices();
  const {
    notificationAppId,
    iamClientId,
    partner,
    getProcessEnv,
    serviceWorkerScripts,
    epicOmni948,
  } = useTenantConfig();
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADED);
  const [notifications, setNotifications] = useState<Array<Noti>>([]);
  const [checkpoint, setCheckpoint] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [markAllReadLoading, setLoadingMarkReadAll] = useState(false);
  const [unRead, setUnread] = useState<number>(0);
  const router = useRouter();
  const [currentTab, setCurrentTab] = useState(NOTIFICATION_TAB.UPDATE_TAB);
  const [isInstallNotificationSuccess, setIsInstallNotificationSuccess] =
    useState(false);
  const notiExternalTypes = useMemo(() => {
    return getExternalTypesByPath(router.query, currentTab);
  }, [router.query, currentTab]);

  const getMoreNotifications = async (
    limit = 10,
    checkpoint: number | undefined = undefined,
  ) => {
    if (!notificationAppId) return;
    const token = localStorage.getItem(FCM_TOKEN);
    const params = {
      app_id: notificationAppId,
      limit,
      ignore_pending_send: 0,
      check_point: checkpoint,
      token: token ?? undefined,
      externalTypes: epicOmni948 ? notiExternalTypes : undefined,
    };
    setLoading(true);

    try {
      const notificationData = await notificationService.getNotification(
        params,
      );
      return convertNotification(notificationData, discoveryService);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Notification với full type
  const { data: notificationData } = useSWR(
    ['/notification', notificationAppId, FCM_TOKEN],
    async () => {
      const token = localStorage.getItem(FCM_TOKEN);
      const allExternalTypes: ExternalType[] = ['noti', 'debt', 'order'];
      const params = {
        app_id: notificationAppId,
        limit: 10,
        ignore_pending_send: 0,
        token: token ?? undefined,
        externalTypes: epicOmni948 ? allExternalTypes : undefined,
      };
      const notificationResponse = await notificationService.getNotification(
        params,
      );
      const notificationData = await convertNotification(
        notificationResponse,
        discoveryService,
      );
      return { total: 3, records: notificationData?.notifications || [] };
    },
    {
      fallbackData: {
        total: 0,
        records: [],
      },
    },
  );

  const getNotifications = async (
    checkpoint: number | undefined = undefined,
    limit = 10,
    isNew = false,
    overrideData: boolean = false,
  ) => {
    const {
      checkpoint: newCheckpoint,
      notifications: newNotifications = [],
      unRead: newUnread,
    } = (await getMoreNotifications(limit, checkpoint)) || {};

    // if epic OMNI-948 flag is enabled
    // if user is not logged in, and access orders noti screen
    // do not call api to get notifications --> set notifications to empty & unread to 0
    if (
      epicOmni948 &&
      !user?.userId &&
      JSON.stringify(notiExternalTypes) === JSON.stringify(['order'])
    ) {
      setNotifications([]);
      setUnread(0);
      return;
    }

    if (!isNew) {
      setCheckpoint(() => newCheckpoint);
      !!overrideData
        ? setNotifications(newNotifications)
        : setNotifications((notifications) => [
            ...notifications,
            ...newNotifications,
          ]);
    } else {
      setNotifications(
        epicOmni948
          ? newNotifications
          : (notifications) => [...newNotifications, ...notifications],
      );
    }
    setUnread(() => newUnread || 0);
  };

  const installWebPushNotification = async () => {
    const firebaseOptions = {
      apiKey: getProcessEnv('FIREBASE_CLOUD_MESSAGING_API_KEY'),
      authDomain: getProcessEnv('FIREBASE_CLOUD_MESSAGING_AUTH_DOMAIN'),
      databaseURL: getProcessEnv('FIREBASE_CLOUD_MESSAGING_DATABASE_URL'),
      projectId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_PROJECT_ID'),
      storageBucket: getProcessEnv('FIREBASE_CLOUD_MESSAGING_STORAGE_BUCKET'),
      messagingSenderId: getProcessEnv(
        'FIREBASE_CLOUD_MESSAGING_MESSAGING_SENDER_ID',
      ),
      appId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_APP_ID'),
      measurementId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_MEASUREMENT_ID'),
    };
    const vapidKey = getProcessEnv('FIREBASE_VAPID_KEY');

    await installNotification({
      user,
      unsAppId: notificationAppId,
      notificationService,
      firebaseOptions,
      vapidKey,
      externalScripts: serviceWorkerScripts,
      onMessage: () => getNotifications(undefined, 1, true),
      markReadNotification,
    });
    setIsInstallNotificationSuccess(true);
    !epicOmni948 && getNotifications(checkpoint);
  };

  useEffect(() => {
    const clientId = iamClientId || getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID');
    const isUserInfoInLS = localStorage.getItem(`tekoid.user.${clientId}`);
    if (
      !userLoading &&
      (user ||
        // user is not logged with partner
        (!isUserInfoInLS && !partner && !terraKit.isAvailable()))
    ) {
      installWebPushNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, userLoading]);

  // if enable epic OMNI-948 flag
  // call getNotifications api to get data when switching screen type (`promotion & update` and `orders`)
  useEffect(() => {
    epicOmni948 &&
      isInstallNotificationSuccess &&
      getNotifications(undefined, 10, false, true);
  }, [notiExternalTypes, epicOmni948, isInstallNotificationSuccess]);

  useEffect(() => {
    if (!loading) {
      if (notifications.length === 0) {
        setStatus(PageStatus.EMPTY);
      } else {
        setStatus(PageStatus.LOADED);
      }
    } else {
      setStatus(PageStatus.LOADING);
    }
  }, [loading, notifications]);

  const markReadNotification = async (
    notification: Pick<Noti, 'id' | 'is_read'>,
  ) => {
    if (!notification.is_read) {
      const token = localStorage.getItem(FCM_TOKEN);
      try {
        const { status } = await notificationService.markNotificationAsRead(
          notification.id,
          token ?? undefined,
        );
        if (status === 'success') {
          setNotifications((prev) =>
            prev.map((noti: Noti) => {
              if (noti.id === notification.id) {
                return {
                  ...noti,
                  is_read: true,
                };
              }
              return noti;
            }),
          );
          if (!notification.is_read)
            setUnread((count: number) => (count ? count - 1 : 0));
        }
      } catch (e) {
        logger.error(e);
      }
    }
  };

  const markReadAll = async (externalTypes?: ExternalType[]) => {
    try {
      if (!notificationAppId) return;
      setLoadingMarkReadAll(true);
      const token = localStorage.getItem(FCM_TOKEN);
      const params = {
        app_id: notificationAppId,
        token: token ?? undefined,
        externalTypes: epicOmni948 ? externalTypes : undefined,
      };
      await notificationService.markReadAll(params);
      setNotifications((notifications) =>
        notifications.map((noti) => ({ ...noti, is_read: true })),
      );
      setUnread(0);
    } catch (e) {
      logger.error(e);
    } finally {
      setLoadingMarkReadAll(false);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        status,
        notifications,
        checkpoint,
        loading,
        getNotifications,
        markReadNotification,
        markReadAll,
        markAllReadLoading,
        unRead,
        currentTab,
        setCurrentTab,
        allNotifications: notificationData?.records || [],
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
export { NOTIFICATION_TAB };
